import { Grid, Pagination, PaginationItem, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import MaterialItem from "./commons/MaterialItem";

import { ArrowBackOutlined, ArrowForwardOutlined } from "@mui/icons-material";

import { Link, useSearchParams } from "react-router-dom";
import {
    fetchList,
    isAllCategories,
    materialCategoryName,
    materialImage,
    pagesFor,
    resultsLabel,
} from "../utils/api";

const LIMIT = 20;

function AllMaterials({ isMobile }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [materials, setMaterials] = useState([]);
    const [total, setTotal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const page = Number(searchParams.get("page")) || 1;
    const search = searchParams.get("search") || "";
    const categorie = searchParams.get("categorie") || "";

    const activeCategorie = isAllCategories(categorie) ? "" : categorie;
    const totalPages = loading && total === null
        ? page
        : pagesFor({ total, itemsLength: materials.length, page, limit: LIMIT });

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);

        fetchList("materials", {
            title: search,
            categorie: activeCategorie,
            limit: LIMIT,
            page: page - 1,
        })
            .then(({ items, total }) => {
                if (cancelled) return;
                setMaterials(items);
                setTotal(total);
            })
            .catch(() => {
                if (cancelled) return;
                setMaterials([]);
                setTotal(0);
                setError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        window.scrollTo({
            top: 0,
            behavior: "smooth", // for smoothly scrolling
        });

        return () => {
            cancelled = true;
        };
    }, [page, search, activeCategorie]);

    function handlePageChange(_, number) {
        const next = { page: number };
        if (search) next.search = search;
        if (categorie) next.categorie = categorie;
        setSearchParams(next);
    }

    const heading = activeCategorie ? materialCategoryName(activeCategorie) : "Materiais";

    const subtitle = (() => {
        if (loading) return "Carregando...";
        if (error) return "Não foi possível carregar os materiais. Tente novamente em instantes.";
        if (total === null) return search ? `Resultados para "${search}"` : "";
        const label = resultsLabel(total, "material", "materiais");
        return search ? `${label} para "${search}"` : label;
    })();

    return (
        <Fragment>
            <Grid
                container
                marginY={6}
                gap={2}
                display={"flex"}
                paddingX={isMobile ? 2 : 8}
            >
                <Grid
                    xs={12}
                    md={12}
                    marginTop={4}
                    color="#010101"
                    borderBottom={1}
                >
                    <Typography
                        component="h2"
                        color="#010101"
                        width={"fit-content"}
                        borderBottom={1}
                        fontFamily="Raleway"
                        fontStyle="normal"
                        fontWeight="800"
                        fontSize="20px"
                        lineHeight="23px"
                    >
                        {heading}
                    </Typography>
                    <Typography
                        role="status"
                        aria-live="polite"
                        color="#4A4A4A"
                        fontFamily="Raleway"
                        fontSize="14px"
                        lineHeight="20px"
                        marginTop={1}
                        marginBottom={1}
                    >
                        {subtitle}
                    </Typography>
                </Grid>

                {!loading && !error && materials.length === 0 && (
                    <Grid xs={12} md={12} padding={"16px 10px"}>
                        <Typography color="#010101" fontFamily="Raleway" fontSize="16px" lineHeight="22px">
                            Nenhum material encontrado
                            {search ? ` para "${search}"` : ""}
                            {activeCategorie ? ` em ${materialCategoryName(activeCategorie)}` : ""}.
                            {" "}
                            <Link to="/materiais" style={{ color: "#C81E1E" }}>
                                Ver todos os materiais
                            </Link>
                        </Typography>
                    </Grid>
                )}

                <Grid xs={12} md={12} display={"flex"} padding={"0px 10px"}>
                    <Grid container marginY={6} gap={2} display={"flex"}>
                        {materials.map((item) => (
                            <Grid
                                xs={12}
                                md={3.5}
                                display={"flex"}
                                padding={"0px 10px"}
                                key={item._id}
                            >
                                <MaterialItem
                                    image={materialImage(item)}
                                    date={item.data}
                                    description={item.titulo}
                                    link={item.link}
                                    categorie={item.categorie}
                                    categorieColor={item.categorieColor}
                                    isMobile={isMobile}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>

            {totalPages > 1 && (
                <Grid
                    xs={12}
                    md={12}
                    padding={"16px 0px"}
                    color="#FFFFFF"
                    borderBottom={1}
                    display={"flex"}
                    justifyContent={"center"}
                >
                    <Pagination
                        color="warning"
                        variant="outlined"
                        count={totalPages}
                        page={Math.min(page, totalPages)}
                        onChange={handlePageChange}
                        showFirstButton
                        showLastButton
                        siblingCount={isMobile ? 0 : 1}
                        renderItem={(item) => (
                            <PaginationItem
                                style={{
                                    borderRadius: 100,
                                    color: "#C81E1E",
                                    padding: "8px 16px",
                                    fontFamily: "Raleway",
                                    fontStyle: "normal",
                                    fontWeight: "600",
                                    fontSize: "16px",
                                    lineHeight: "20px",
                                }}
                                slots={{ previous: ArrowBackOutlined, next: ArrowForwardOutlined }}
                                {...item}
                            />
                        )}
                    />
                </Grid>
            )}
        </Fragment>
    );
}

export default AllMaterials;
